import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';
import { AutoCompleteMultiSelect } from './AutoCompleteMultiSelect';
import { AutoCompleteTextInput } from './AutoCompleteTextInput';

interface PresetFilterFormProps {
  campaignId: string;
  onComplete: (data: any) => void;
  onBack?: () => void;
}

interface FilterOptions {
  location?: {
    locations?: string[];
    countries?: string[];
    states?: string[];
  };
  industry?: {
    industries?: string[];
    companies?: string[];
    sizes?: string[];
  };
  position?: {
    titles?: string;
    seniority?: string[];
    skills?: string[];
    functions?: string[];
  };
}

export function PresetFilterForm({ campaignId, onComplete, onBack }: PresetFilterFormProps) {
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [selectedFilters, setSelectedFilters] = useState<FilterOptions>({});
  const [targetListName, setTargetListName] = useState('');
  const [activeCategory, setActiveCategory] = useState('location');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [filterRequestId, setFilterRequestId] = useState<string | null>(null);
  const [existingRequest, setExistingRequest] = useState<any>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  useEffect(() => {
    fetchFilterOptions();
    checkExistingFilterRequest();
  }, [campaignId]);

  const checkExistingFilterRequest = async () => {
    if (!campaignId) {
      setCheckingExisting(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('preset_filter_requests')
        .select('*')
        .eq('campaign_id', campaignId)
        .single();

      if (data && !error) {
        setExistingRequest(data);
        setFilterRequestId(data.id);
        setTargetListName(data.request_name || '');
        setSelectedFilters(data.filters || {});
        setIsSubmitted(true); // Show as already submitted
      }
    } catch (error) {
      console.error('Error checking existing filter request:', error);
    } finally {
      setCheckingExisting(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await supabase.functions.invoke('get-filter-options', {
        body: { limit: 200 }
      });
      
      if (response.data?.data) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleSubmit = async () => {
    if (!targetListName) {
      alert('Please provide a name for your target lead list');
      return;
    }

    // With draft-first pattern, we should always have a campaign ID
    if (!campaignId) {
      alert('Error: Campaign must be initialized first. Please go back to Step 1.');
      return;
    }

    setLoading(true);
    
    // Debug: Log what we're sending
    const requestBody = {
      campaign_id: campaignId,
      filters: selectedFilters,
      request_name: targetListName,
      target_lead_count: 100, // Default value since we removed the field
      priority: 'normal' // Changed to 'normal' to match preset_filter_requests constraint
    };
    console.log('Sending filter request with:', requestBody);
    
    try {
      const response = await supabase.functions.invoke('create-filter-request', {
        body: requestBody
      });

      console.log('Filter request response:', response);
      
      if (response.data?.success) {
        setIsSubmitted(true);
        setFilterRequestId(response.data.data.id);
        
        // Create notification for assigned CSR if exists
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            // Get user's assigned CSR
            const { data: userData } = await supabase
              .from('users')
              .select('full_name, assigned_csr')
              .eq('id', session.user.id)
              .single();
            
            if (userData?.assigned_csr) {
              // Create notification for CSR
              await supabase
                .from('notifications')
                .insert({
                  user_id: userData.assigned_csr,
                  type: 'filter_request_submitted',
                  title: 'New Target Lead Request',
                  message: `${userData.full_name || 'A user'} submitted a target lead request: "${targetListName}"`,
                  data: {
                    filter_request_id: response.data.data.id,
                    campaign_id: campaignId,
                    user_id: session.user.id,
                    request_name: targetListName
                  }
                });
            }
          }
        } catch (notificationError) {
          console.error('Error creating CSR notification:', notificationError);
          // Don't fail the whole operation if notification fails
        }
        
        onComplete({
          type: 'preset_filter',
          data: response.data.data
        });
      } else if (response.error) {
        // Handle Supabase function invocation errors
        console.error('Filter request invocation error:', response.error);
        throw new Error(response.error.message || 'Failed to create filter request');
      } else {
        console.error('Filter request failed:', response.data);
        throw new Error(response.data?.error || 'Failed to create filter request');
      }
    } catch (error: any) {
      console.error('Error creating filter request:', error);
      alert(error.message || 'Failed to create filter request');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (category: string, subcategory: string, value: any) => {
    setSelectedFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof FilterOptions],
        [subcategory]: value
      }
    }));
  };

  const categories = [
    { id: 'location', label: 'Location', icon: '📍' },
    { id: 'industry', label: 'Industry', icon: '🏢' },
    { id: 'position', label: 'Position', icon: '💼' }
  ];

  // Helper function to check if any filters are selected
  const hasSelectedFilters = () => {
    return Object.entries(selectedFilters).some(([_, categoryFilters]) => {
      return Object.values(categoryFilters as any).some(value => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim().length > 0;
        return false;
      });
    });
  };

  // Build filter summary display
  const renderFilterSummary = () => {
    const summaryCards = [];
    
    // Location Summary
    if (selectedFilters.location) {
      const locationItems = [];
      if (selectedFilters.location.locations?.length) {
        locationItems.push(`Locations: ${selectedFilters.location.locations.join(', ')}`);
      }
      if (selectedFilters.location.countries?.length) {
        locationItems.push(`Countries: ${selectedFilters.location.countries.join(', ')}`);
      }
      if (selectedFilters.location.states?.length) {
        locationItems.push(`States: ${selectedFilters.location.states.join(', ')}`);
      }
      
      if (locationItems.length > 0) {
        summaryCards.push(
          <div key="location" className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center mb-2">
              <span className="text-lg mr-2">📍</span>
              <h4 className="font-medium text-gray-900">Location</h4>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {locationItems.map((item, idx) => (
                <div key={idx}>{item}</div>
              ))}
            </div>
          </div>
        );
      }
    }
    
    // Industry Summary
    if (selectedFilters.industry) {
      const industryItems = [];
      if (selectedFilters.industry.industries?.length) {
        industryItems.push(`Industries: ${selectedFilters.industry.industries.join(', ')}`);
      }
      if (selectedFilters.industry.companies?.length) {
        industryItems.push(`Companies: ${selectedFilters.industry.companies.join(', ')}`);
      }
      if (selectedFilters.industry.sizes?.length) {
        industryItems.push(`Company Size: ${selectedFilters.industry.sizes.join(', ')}`);
      }
      
      if (industryItems.length > 0) {
        summaryCards.push(
          <div key="industry" className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center mb-2">
              <span className="text-lg mr-2">🏢</span>
              <h4 className="font-medium text-gray-900">Industry</h4>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {industryItems.map((item, idx) => (
                <div key={idx}>{item}</div>
              ))}
            </div>
          </div>
        );
      }
    }
    
    // Position Summary
    if (selectedFilters.position) {
      const positionItems = [];
      if (selectedFilters.position.titles) {
        positionItems.push(`Job Titles: ${selectedFilters.position.titles}`);
      }
      if (selectedFilters.position.seniority?.length) {
        positionItems.push(`Seniority: ${selectedFilters.position.seniority.join(', ')}`);
      }
      if (selectedFilters.position.skills?.length) {
        positionItems.push(`Skills: ${selectedFilters.position.skills.join(', ')}`);
      }
      if (selectedFilters.position.functions?.length) {
        positionItems.push(`Functions: ${selectedFilters.position.functions.join(', ')}`);
      }
      
      if (positionItems.length > 0) {
        summaryCards.push(
          <div key="position" className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center mb-2">
              <span className="text-lg mr-2">💼</span>
              <h4 className="font-medium text-gray-900">Position</h4>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {positionItems.map((item, idx) => (
                <div key={idx}>{item}</div>
              ))}
            </div>
          </div>
        );
      }
    }
    
    return summaryCards;
  };

  // Show loading state while checking for existing request
  if (checkingExisting) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading filter request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Target Lead selection</h2>
      </div>

      {/* Success Banner */}
      {isSubmitted && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                {existingRequest ? 'Filter Request Already Exists' : 'Filter Request Submitted Successfully!'}
              </h3>
              <p className="mt-1 text-sm text-green-700">
                {existingRequest 
                  ? 'This campaign already has a filter request. The existing request details are shown below. You can proceed to the next step.'
                  : 'Your filter request has been sent to our CSR team. They will process your request and build your lead list. You can now proceed to the next step.'}
              </p>
              {filterRequestId && (
                <p className="mt-1 text-xs text-green-600">
                  Request ID: {filterRequestId}
                  {existingRequest && existingRequest.status && (
                    <span className="ml-2">Status: {existingRequest.status}</span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info Banner - Show only if not submitted */}
      {!isSubmitted && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-blue-700">
            Define your target lead criteria below. Our CSR team will build your target lead list using the criteria you give us.
          </p>
        </div>
      )}

      {/* Target Lead List Name */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Lead List Name *
          </label>
          <input
            type="text"
            value={targetListName}
            onChange={(e) => setTargetListName(e.target.value)}
            placeholder="e.g., Q1 2025 Enterprise Targets"
            disabled={isSubmitted}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Filter Categories - Only 3 tabs now */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <div className="flex space-x-1 p-1">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                disabled={isSubmitted}
                className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${isSubmitted ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Location Filters (formerly Geography) */}
          {activeCategory === 'location' && (
            <div className="space-y-4">
              <AutoCompleteTextInput
                label="Locations"
                placeholder="e.g., San Francisco, New York, London"
                suggestions={filterOptions.geography?.city?.map((opt: any) => opt.label) || []}
                value={selectedFilters.location?.locations?.join(', ') || ''}
                onChange={(value) => updateFilter('location', 'locations', 
                  value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                allowCustom={true}
                disabled={isSubmitted}
              />
              <AutoCompleteTextInput
                label="Countries"
                placeholder="Start typing country name..."
                suggestions={filterOptions.geography?.country?.map((opt: any) => opt.label) || [
                  'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 
                  'Australia', 'Japan', 'India', 'Brazil', 'Mexico'
                ]}
                value={selectedFilters.location?.countries?.join(', ') || ''}
                onChange={(value) => updateFilter('location', 'countries',
                  value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                allowCustom={true}
                disabled={isSubmitted}
              />
              <AutoCompleteTextInput
                label="States/Regions"
                placeholder="e.g., California, Texas, Ontario"
                suggestions={filterOptions.geography?.state?.map((opt: any) => opt.label) || [
                  'California', 'Texas', 'New York', 'Florida', 'Illinois',
                  'Pennsylvania', 'Ohio', 'Georgia', 'North Carolina', 'Michigan',
                  'Ontario', 'Quebec', 'British Columbia', 'Alberta'
                ]}
                value={selectedFilters.location?.states?.join(', ') || ''}
                onChange={(value) => updateFilter('location', 'states',
                  value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                allowCustom={true}
                disabled={isSubmitted}
              />
            </div>
          )}

          {/* Industry Filters (formerly Company) */}
          {activeCategory === 'industry' && (
            <div className="space-y-4">
              <AutoCompleteMultiSelect
                label="Industries"
                options={filterOptions.company?.industry || []}
                selectedValues={selectedFilters.industry?.industries || []}
                onChange={(values) => updateFilter('industry', 'industries', values)}
                placeholder="e.g., Technology, Healthcare, Finance"
                disabled={isSubmitted}
              />
              <AutoCompleteTextInput
                label="Company Names"
                placeholder="e.g., Google, Microsoft, Apple"
                suggestions={filterOptions.company?.names?.map((opt: any) => opt.label) || []}
                value={selectedFilters.industry?.companies?.join(', ') || ''}
                onChange={(value) => updateFilter('industry', 'companies',
                  value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                allowCustom={true}
                disabled={isSubmitted}
              />
              <AutoCompleteMultiSelect
                label="Company Size"
                options={filterOptions.company?.size || []}
                selectedValues={selectedFilters.industry?.sizes || []}
                onChange={(values) => updateFilter('industry', 'sizes', values)}
                placeholder="e.g., 1-50, 50-500, 500+"
                disabled={isSubmitted}
              />
            </div>
          )}

          {/* Position Filters (formerly Role) - Now includes Skills */}
          {activeCategory === 'position' && (
            <div className="space-y-4">
              <AutoCompleteTextInput
                label="Job Titles"
                placeholder="e.g., CEO, VP Sales, General Manager"
                suggestions={[
                  'VP Sales', 'Director of Sales', 'Chief Revenue Officer',
                  'VP Marketing', 'Director of Marketing', 'CMO',
                  'CEO', 'COO', 'CFO', 'CTO',
                  'General Manager', 'Account Executive', 'Sales Manager', 
                  'Business Development Manager', 'Director of Operations'
                ]}
                value={selectedFilters.position?.titles || ''}
                onChange={(value) => updateFilter('position', 'titles', value)}
                allowCustom={true}
                disabled={isSubmitted}
              />
              <AutoCompleteMultiSelect
                label="Seniority Levels"
                options={filterOptions.role?.seniority || [
                  { value: 'entry', label: 'Entry Level' },
                  { value: 'senior', label: 'Senior' },
                  { value: 'manager', label: 'Manager' },
                  { value: 'director', label: 'Director' },
                  { value: 'vp', label: 'VP' },
                  { value: 'evp', label: 'EVP' },
                  { value: 'c-level', label: 'C-Level' },
                  { value: 'owner', label: 'Owner' },
                  { value: 'partner', label: 'Partner' }
                ]}
                selectedValues={selectedFilters.position?.seniority || []}
                onChange={(values) => updateFilter('position', 'seniority', values)}
                placeholder="e.g., Director, VP, C-Level"
                disabled={isSubmitted}
              />
              <AutoCompleteTextInput
                label="Skills"
                placeholder="e.g., Sales Leadership, B2B Sales, SaaS"
                suggestions={[
                  'Sales Leadership', 'B2B Sales', 'Enterprise Sales', 'SaaS',
                  'Cloud Computing', 'Digital Marketing', 'Product Management',
                  'Data Analysis', 'Machine Learning', 'Cybersecurity', 'DevOps',
                  'Business Development', 'Strategic Planning', 'Account Management'
                ]}
                value={selectedFilters.position?.skills?.join(', ') || ''}
                onChange={(value) => updateFilter('position', 'skills',
                  value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                allowCustom={true}
                disabled={isSubmitted}
              />
              <AutoCompleteMultiSelect
                label="Functions"
                options={filterOptions.role?.function || [
                  { value: 'sales', label: 'Sales' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'business-development', label: 'Business Development' },
                  { value: 'operations', label: 'Operations' },
                  { value: 'engineering', label: 'Engineering' },
                  { value: 'product', label: 'Product' },
                  { value: 'finance', label: 'Finance' },
                  { value: 'hr', label: 'Human Resources' },
                  { value: 'legal', label: 'Legal' },
                  { value: 'customer-success', label: 'Customer Success' },
                  { value: 'it', label: 'IT' },
                  { value: 'general-management', label: 'General Management' }
                ]}
                selectedValues={selectedFilters.position?.functions || []}
                onChange={(values) => updateFilter('position', 'functions', values)}
                placeholder="e.g., Sales, Marketing, Operations"
                disabled={isSubmitted}
              />
            </div>
          )}
        </div>
      </div>

      {/* Filter Criteria Summary - Live updating display */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Filter Criteria Summary 
          <span className="text-sm font-normal text-gray-500 ml-2">(Updates as you select)</span>
        </h3>
        
        {hasSelectedFilters() ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderFilterSummary()}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No filters selected yet. Start by selecting criteria from the tabs above.</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <div className="flex space-x-2">
          {onBack && (
            <button
              onClick={onBack}
              disabled={isSubmitted}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => {
              // Save as draft functionality
              console.log('Save as draft clicked');
            }}
            disabled={isSubmitted || !targetListName}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save as Draft
          </button>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={onBack}
            disabled={isSubmitted}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              disabled={loading || !targetListName}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Request...' : 'Submit Request'}
            </button>
          ) : (
            <button
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              onClick={() => {
                // Call the onComplete callback again with shouldAdvance flag
                onComplete({
                  type: 'preset_filter',
                  data: {
                    id: filterRequestId,
                    shouldAdvance: true  // Signal to advance to next step
                  }
                });
              }}
            >
              Proceed to Next Step →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}